import { Form } from "@inertiajs/react"
import { Trash2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { creatorCollabSubmissionPath } from "@/routes"
import type { Submission } from "@/types/submissions"

/**
 * OKS-35 — Creator-side delete-submission confirm dialog (UX Spec §5).
 *
 * Inertia `<Form method="delete">`. Hard-deletes the row server-side
 * (only on `pending` / `flagged`; service guards on `approved`).
 */
interface Props {
  submission: Submission
  trigger?: React.ReactElement
  /**
   * Controlled open state. When provided, the dialog renders no
   * `DialogTrigger` — the caller owns the affordance (e.g. a
   * `DropdownMenuItem`). Omit for the standalone/uncontrolled variant.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function DeleteSubmissionConfirmDialog({
  submission,
  trigger,
  open: controlledOpen,
  onOpenChange,
}: Props) {
  const { t } = useTranslation()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen
  const setOpen = (value: boolean) => {
    if (!isControlled) setUncontrolledOpen(value)
    onOpenChange?.(value)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger
          render={
            trigger ?? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid={`delete-submission-trigger-${submission.id}`}
              >
                <Trash2 aria-hidden="true" className="mr-1 size-4" />
                {t("submissions.creator.delete_kebab")}
              </Button>
            )
          }
        />
      )}
      <DialogContent
        data-testid={`delete-submission-dialog-${submission.id}`}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>
            {t("submissions.creator.confirm.delete_title")}
          </DialogTitle>
          <DialogDescription>
            {t("submissions.creator.confirm.delete_body")}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={creatorCollabSubmissionPath(
            submission.collab_id,
            submission.id,
          )}
          method="delete"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid={`delete-submission-cancel-${submission.id}`}
              >
                {t("submissions.creator.confirm.cancel")}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={processing}
                data-testid={`delete-submission-confirm-${submission.id}`}
              >
                {t("submissions.creator.confirm.delete_cta")}
              </Button>
            </DialogFooter>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
